import type {
  StockConfigFilters,
  SafetyStockForm,
  SafetyStock,
  StockConfigData
} from '@/pages/stock-config/types/stock-config.types';
import { authFetch } from './api.service';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

export interface StockConfigApiResponse extends StockConfigData {}

export interface LocationApiResponse {
  success: boolean;
  data: Array<{
    id: string;
    nombre: string;
    direccion: string;
    telefono: string;
    email: string;
    estado: string;
    tipoLocal: string;
  }>;
}

export interface CategoryApiResponse {
  success: boolean;
  data: Array<{
    id: number;
    name: string;
  }>;
}

export interface BulkUpdateItem {
  id: string;
  minStock: number;
}

export interface BulkUpdateResponse {
  success: boolean;
  message: string;
  results: Array<{ id: string; success: boolean }>;
  errors: Array<{ id: string; error: string }>;
}

export interface TransferStockItem {
  id: string;
  ingredient: {
    id: number;
    name: string;
    unit: string;
    category: string;
  };
  ingredientId: number;
  locationId: number;
  locationName: string;
  dayOfWeek: string;
  shift: string;
  minStock: number;
  unit: string;
  createdAt: string;
  updatedAt: string;
}

export interface TransferConfigApiResponse {
  transferStocks: {
    items: TransferStockItem[];
    totalItems: number;
    totalCount: number;
    page: number;
    limit: number;
  };
}

class StockConfigService {
  private baseUrl = `${API_BASE_URL}/stock-config`;

  // Obtener configuraciones de stock con filtros
  async getStockConfig(
    filters: StockConfigFilters = {},
    page: number = 1,
    limit: number = 10
  ): Promise<StockConfigApiResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    // Añadir filtros a los parámetros
    if (filters.ingredientName) {
      params.append('ingredientName', filters.ingredientName);
    }
    if (filters.locationId) {
      params.append('locationId', filters.locationId);
    }
    if (filters.categoryId) {
      params.append('categoryId', filters.categoryId);
    }
    if (filters.dayOfWeek) {
      params.append('dayOfWeek', filters.dayOfWeek);
    }
    if (filters.shift) {
      params.append('shift', filters.shift);
    }

    const response = await authFetch(`${this.baseUrl}?${params.toString()}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  // Crear configuración de stock de seguridad
  async createSafetyStock(data: SafetyStockForm): Promise<SafetyStock> {
    const response = await authFetch(`${this.baseUrl}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.data;
  }

  // Actualizar configuración de stock de seguridad
  async updateSafetyStock(id: string, data: Partial<SafetyStockForm>): Promise<SafetyStock> {
    const response = await authFetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.data;
  }

  // Eliminar configuración de stock de seguridad
  async deleteSafetyStock(id: string): Promise<void> {
    const response = await authFetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  // Obtener ubicaciones desde la API
  async getLocations(): Promise<LocationApiResponse> {
    const locationsUrl = `${API_BASE_URL}/locations`;

    const response = await authFetch(locationsUrl, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  // Obtener categorías de ingredientes
  async getCategories(): Promise<CategoryApiResponse> {
    const response = await authFetch(`${this.baseUrl}/categories`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  // Actualizar múltiples configuraciones de stock (bulk update)
  async bulkUpdateSafetyStock(updates: BulkUpdateItem[]): Promise<BulkUpdateResponse> {
    const response = await authFetch(`${this.baseUrl}/bulk`, {
      method: 'PUT',
      body: JSON.stringify({ updates }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  // Obtener configuraciones de transferencia con filtros
  async getTransferConfig(
    filters: StockConfigFilters = {},
    page: number = 1,
    limit: number = 10
  ): Promise<TransferConfigApiResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    // Añadir filtros a los parámetros
    if (filters.ingredientName) {
      params.append('ingredientName', filters.ingredientName);
    }
    if (filters.locationId) {
      params.append('locationId', filters.locationId);
    }
    if (filters.categoryId) {
      params.append('categoryId', filters.categoryId);
    }
    if (filters.dayOfWeek) {
      params.append('dayOfWeek', filters.dayOfWeek);
    }
    if (filters.shift) {
      params.append('shift', filters.shift);
    }

    const response = await authFetch(`${API_BASE_URL}/transfer-config?${params.toString()}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  // Crear configuración de stock de seguridad para transferencias
  async createTransferStock(data: {
    ingredientId: string | number;
    locationId: string | number;
    dayOfWeek: string;
    shift: string;
    minStock: number;
  }): Promise<TransferStockItem> {
    const response = await authFetch(`${API_BASE_URL}/transfer-config`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.data;
  }

  // Actualizar múltiples configuraciones de transferencia (bulk update)
  async bulkUpdateTransferStock(updates: BulkUpdateItem[]): Promise<BulkUpdateResponse> {
    const response = await authFetch(`${API_BASE_URL}/transfer-config/bulk`, {
      method: 'PUT',
      body: JSON.stringify({ updates }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }
}

export const stockConfigService = new StockConfigService();